/**
 * CoachingDepthSelector (PR-45, AI-12). Shared by form + chat intake.
 *
 * Three depths (coachingDepthSchema): Recommend / Show me / Build it with me.
 * The selector only records the subscriber's preference on the task row; the
 * renderer reads it later to set verbosity (ARCH-005 §5). It is not a
 * shape-check input in V1 (ARCH-009 OQ-S1).
 */

import type { CoachingDepth } from '@studio/api-schemas';

const OPTIONS: ReadonlyArray<{
  value: CoachingDepth;
  label: string;
  hint: string;
}> = [
  {
    value: 'recommend',
    label: 'Recommend',
    hint: 'Tell me what you would do. I want the call, not the working.',
  },
  {
    value: 'show_me',
    label: 'Show me',
    hint: 'The recommendation plus the reasoning behind it.',
  },
  {
    value: 'build_it_with_me',
    label: 'Build it with me',
    hint: 'Walk me through it so I can do the next one myself.',
  },
];

export function CoachingDepthSelector({
  value,
  onChange,
}: {
  value: CoachingDepth;
  onChange: (v: CoachingDepth) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-[color:var(--color-black)]">
        How much do you want to see?
      </legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {OPTIONS.map((opt) => {
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              className={[
                'cursor-pointer rounded-lg border-[1.5px] p-3 text-sm transition-colors',
                selected
                  ? 'border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)]'
                  : 'border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] hover:bg-[color:var(--color-linen)]',
              ].join(' ')}
            >
              <input
                type="radio"
                name="coaching-depth"
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="sr-only"
              />
              <span className="block font-medium text-[color:var(--color-black)]">
                {opt.label}
              </span>
              <span className="mt-1 block text-xs leading-relaxed text-[color:var(--color-text-mute)]">
                {opt.hint}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
